"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  BinaryWorking,
  BitwiseView,
} from "@/components/tools/cs-tools/binary-working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type ArithNote,
  type ArithOp,
  type BitwiseOp,
  WIDTHS,
} from "@/lib/tool-kit/cs-tools/binary-calc";
import {
  type ArithOk,
  type BitsOk,
  type EvalInput,
  evaluateBinary,
  usesCount,
  usesSecondOperand,
} from "@/lib/tool-kit/cs-tools/binary-eval";
import {
  groupNibbles,
  signedBinary,
  signedHex,
  signedOctal,
} from "@/lib/tool-kit/cs-tools/bits";

/** English strings. A translated content file overrides these through `ui`. */
export const BINARY_CALCULATOR_STRINGS = {
  modeLabel: "Kind of operation",
  modeArithmetic: "Arithmetic",
  modeBitwise: "Bitwise",
  operationLabel: "Operation",
  opAdd: "Add (A + B)",
  opSubtract: "Subtract (A − B)",
  opMultiply: "Multiply (A × B)",
  opDivide: "Divide (A ÷ B)",
  bitAnd: "AND",
  bitOr: "OR",
  bitXor: "XOR",
  bitNand: "NAND",
  bitNor: "NOR",
  bitXnor: "XNOR",
  bitNot: "NOT (A only)",
  bitShl: "Shift left (A << n)",
  bitShr: "Shift right, logical (A >> n)",
  bitSar: "Shift right, arithmetic (keeps the sign bit)",
  bitRol: "Rotate left",
  bitRor: "Rotate right",
  labelA: "First number (A)",
  labelB: "Second number (B)",
  hintArithmetic:
    "Binary digits only, with an optional leading minus. Spaces and underscores are ignored, so 1011 0110 works.",
  hintBitwise:
    "A bit pattern no wider than the width chosen below. Spaces and underscores are ignored.",
  placeholderBinary: "1101",
  defaultA: "1101",
  defaultB: "101",
  labelCount: "Shift or rotate distance (a whole number in decimal)",
  defaultCount: "3",
  labelWidth: "Width",
  widthOption: "{bits} bits",
  errEmptyA: "Enter a binary number for A.",
  errEmptyB: "Enter a binary number for B.",
  errInvalidA:
    "A is not a binary number. Use only the digits 0 and 1, with an optional minus sign.",
  errInvalidB:
    "B is not a binary number. Use only the digits 0 and 1, with an optional minus sign.",
  errNegativeBitwise:
    "Bitwise operations take a plain bit pattern, so leave out the minus sign. For negative values use the two's complement calculator.",
  errDivideZero:
    "Division by zero is undefined. Change B to a non-zero number.",
  errTooWide: "A and B must each fit in the chosen width of {width} bits.",
  errCount: "Enter the distance as a whole number of 0 or more, up to 4096.",
  resultsHeading: "Result",
  resultBinary: "Binary",
  resultDecimal: "Decimal",
  resultHex: "Hexadecimal",
  resultOctal: "Octal",
  resultRemainder: "Remainder (binary)",
  resultUnsigned: "Unsigned value",
  resultSigned: "Signed value (two's complement)",
  resultLength: "Length",
  bitCount: "one: {count} bit|other: {count} bits",
  copyBinary: "Copy binary",
  copyDecimal: "Copy decimal",
  copied: "Copied!",
  workingHeading: "Working",
  addTitle: "Column addition with carries",
  subtractTitle: "Column subtraction with borrows",
  multiplyTitle: "Shift and add: one partial product for each 1 in B",
  divideTitle: "Long division, one bit of A at a time",
  bitwiseTitle: "Bit-by-bit comparison",
  rowLabelColumn: "Row",
  rowCarry: "Carry in",
  rowBorrow: "Borrow in",
  rowA: "A",
  rowB: "B",
  rowSum: "Sum",
  rowLarger: "Larger",
  rowSmaller: "Smaller",
  rowDifference: "Difference",
  rowResult: "Result",
  colMultiplierBit: "Bit of B",
  colPartial: "Partial product (A shifted left)",
  colRunning: "Running total",
  colPosition: "Bit position",
  colBringDown: "Bit brought down",
  colPartial2: "Remainder so far",
  colFits: "Divisor fits?",
  colQuotientBit: "Quotient bit",
  colRemainder: "New remainder",
  colRow: "Row",
  colBinary: "Binary",
  colHex: "Hex",
  yes: "Yes, subtract",
  no: "No",
  multiplyByZero:
    "B is zero, so there are no partial products and the product is 0.",
  shiftDistanceLabel: "Distance:",
  shiftedOutLabel: "bits shifted out:",
  noteMixedSigns:
    "The signs differ, so the columns subtract the smaller magnitude from the larger, and the result takes the sign of the larger operand.",
  noteSignApplied:
    "The columns work on the magnitudes. The result is negative because exactly one operand is negative.",
  noteTruncated:
    "Division truncates toward zero, so the remainder takes the sign of A — the convention in C, Java and JavaScript.",
  noteNoWorking:
    "Column working is shown for operands up to 64 bits long. The result above is still exact.",
  enterPrompt: "Enter A and B to see the result and the working.",
} as const;

type Strings = typeof BINARY_CALCULATOR_STRINGS;
type Mode = "arithmetic" | "bitwise";

const ARITH_OPS: ArithOp[] = ["add", "subtract", "multiply", "divide"];
const BITWISE_OPS: BitwiseOp[] = [
  "and",
  "or",
  "xor",
  "nand",
  "nor",
  "xnor",
  "not",
  "shl",
  "shr",
  "sar",
  "rol",
  "ror",
];

const ARITH_LABEL: Record<ArithOp, keyof Strings> = {
  add: "opAdd",
  subtract: "opSubtract",
  multiply: "opMultiply",
  divide: "opDivide",
};

const BITWISE_LABEL: Record<BitwiseOp, keyof Strings> = {
  and: "bitAnd",
  or: "bitOr",
  xor: "bitXor",
  nand: "bitNand",
  nor: "bitNor",
  xnor: "bitXnor",
  not: "bitNot",
  shl: "bitShl",
  shr: "bitShr",
  sar: "bitSar",
  rol: "bitRol",
  ror: "bitRor",
};

const NOTE_KEY: Record<ArithNote, keyof Strings> = {
  mixedSigns: "noteMixedSigns",
  signApplied: "noteSignApplied",
  truncated: "noteTruncated",
};

function optionList<K extends string>(
  keys: readonly K[],
  labels: Record<K, keyof Strings>,
  strings: Strings
): Option<K>[] {
  return keys.map((key) => ({ value: key, label: strings[labels[key]] }));
}

export default function BinaryCalculatorWidget() {
  const strings = useToolUi(BINARY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("arithmetic");
  const [arithOp, setArithOp] = useState<ArithOp>("add");
  const [bitOp, setBitOp] = useState<BitwiseOp>("and");
  const [aText, setAText] = useState<string>(strings.defaultA);
  const [bText, setBText] = useState<string>(strings.defaultB);
  const [countText, setCountText] = useState<string>(strings.defaultCount);
  const [width, setWidth] = useState<string>("8");

  const input: EvalInput = {
    mode,
    arithOp,
    bitOp,
    aText,
    bText,
    countText,
    width: Number(width),
  };
  const needsB = usesSecondOperand(input);
  const showCount = usesCount(input);
  const view = evaluateBinary(input);

  const widthOptions: Option<string>[] = WIDTHS.map((bits) => ({
    value: String(bits),
    label: t(strings.widthOption, { bits: fmt(bits) }),
  }));

  const errorText =
    view.kind === "error"
      ? t(strings[view.key], { width: fmt(Number(width)) })
      : null;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="bincalc-mode"
        onChange={setMode}
        options={[
          { value: "arithmetic", label: strings.modeArithmetic },
          { value: "bitwise", label: strings.modeBitwise },
        ]}
        value={mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "arithmetic" ? (
          <SelectField
            id="bincalc-arith-op"
            label={strings.operationLabel}
            onChange={setArithOp}
            options={optionList(ARITH_OPS, ARITH_LABEL, strings)}
            value={arithOp}
          />
        ) : (
          <SelectField
            id="bincalc-bit-op"
            label={strings.operationLabel}
            onChange={setBitOp}
            options={optionList(BITWISE_OPS, BITWISE_LABEL, strings)}
            value={bitOp}
          />
        )}
        {mode === "bitwise" ? (
          <SelectField
            id="bincalc-width"
            label={strings.labelWidth}
            onChange={setWidth}
            options={widthOptions}
            value={width}
          />
        ) : null}
        <TextField
          hint={
            mode === "arithmetic" ? strings.hintArithmetic : strings.hintBitwise
          }
          id="bincalc-a"
          label={strings.labelA}
          mono
          onChange={setAText}
          placeholder={strings.placeholderBinary}
          value={aText}
        />
        {needsB ? (
          <TextField
            id="bincalc-b"
            label={strings.labelB}
            mono
            onChange={setBText}
            placeholder={strings.placeholderBinary}
            value={bText}
          />
        ) : null}
        {showCount ? (
          <TextField
            id="bincalc-count"
            label={strings.labelCount}
            numeric
            onChange={setCountText}
            value={countText}
          />
        ) : null}
      </div>

      <div aria-live="polite" className="space-y-5">
        {errorText ? <ErrorNotice>{errorText}</ErrorNotice> : null}
        {view.kind === "arith" ? (
          <ArithmeticResult
            fmt={fmt}
            locale={locale}
            outcome={view.outcome}
            strings={strings}
          />
        ) : null}
        {view.kind === "bits" ? (
          <BitwiseResult
            count={showCount ? fmt(view.outcome.count ?? 0) : null}
            fmt={fmt}
            locale={locale}
            outcome={view.outcome}
            strings={strings}
          />
        ) : null}
      </div>
    </div>
  );
}

type Fmt = ReturnType<typeof useToolNumber>;

function ArithmeticResult({
  outcome,
  strings,
  fmt,
  locale,
}: {
  outcome: ArithOk;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const { value, remainder, working, notes } = outcome;
  const binary = signedBinary(value);
  const length = value < BigInt(0) ? -value : value;
  const bitLen = length.toString(2).length;
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultBinary}
          value={groupNibbles(binary)}
        />
        <StatCard label={strings.resultDecimal} value={value.toString()} />
        <StatCard label={strings.resultHex} value={signedHex(value)} />
        <StatCard label={strings.resultOctal} value={signedOctal(value)} />
        <StatCard
          label={strings.resultLength}
          value={tPlural(strings.bitCount, locale, bitLen, {
            count: fmt(bitLen),
          }).trim()}
        />
        {remainder === null ? null : (
          <StatCard
            label={strings.resultRemainder}
            value={groupNibbles(signedBinary(remainder))}
          />
        )}
      </StatGrid>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyBinary}
          text={binary}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyDecimal}
          text={value.toString()}
        />
      </div>
      {notes.map((note) => (
        <Notice key={note}>{strings[NOTE_KEY[note]]}</Notice>
      ))}
      {working ? (
        <BinaryWorking strings={strings} working={working} />
      ) : (
        <Notice>{strings.noteNoWorking}</Notice>
      )}
    </>
  );
}

function BitwiseResult({
  outcome,
  strings,
  count,
  fmt,
  locale,
}: {
  outcome: BitsOk;
  strings: Strings;
  count: string | null;
  fmt: Fmt;
  locale: string;
}) {
  const bits = outcome.result.toString(2).padStart(outcome.width, "0");
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.resultBinary}
          value={groupNibbles(bits)}
        />
        <StatCard
          label={strings.resultHex}
          value={outcome.result.toString(16).toUpperCase()}
        />
        <StatCard
          label={strings.resultUnsigned}
          value={outcome.result.toString()}
        />
        <StatCard
          label={strings.resultSigned}
          value={outcome.signed.toString()}
        />
        <StatCard
          label={strings.resultLength}
          value={tPlural(strings.bitCount, locale, outcome.width, {
            count: fmt(outcome.width),
          }).trim()}
        />
      </StatGrid>
      <div className="flex flex-wrap gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyBinary}
          text={bits}
        />
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyDecimal}
          text={outcome.result.toString()}
        />
      </div>
      <BitwiseView count={count} outcome={outcome} strings={strings} />
    </>
  );
}
